import { z } from 'zod';
import { ACADEMIC_PERIOD_STATUS_VALUES, TERM_TYPE_VALUES } from '@fas/types';
import {
  booleanQuerySchema,
  codeSchema,
  effectiveDatesShape,
  isoDateSchema,
  nonEmptyTrimmed,
  optionalText,
  paginationQuerySchema,
  refineEffectiveRange,
  uuidSchema,
} from './common';

function refineDateRange(v: { startDate?: string; endDate?: string }, ctx: z.RefinementCtx) {
  if (v.startDate && v.endDate && v.endDate <= v.startDate) {
    ctx.addIssue({ code: 'custom', path: ['endDate'], message: 'End date must be after the start date' });
  }
}

// ── Academic year ──────────────────────────────────────────────────────────────
const academicYearBase = z.object({
  institutionId: uuidSchema.optional(),
  code: codeSchema(20),
  name: nonEmptyTrimmed(100, 'Name'),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
  active: z.boolean().optional(),
});
export const createAcademicYearSchema = academicYearBase.superRefine(refineDateRange);
export const updateAcademicYearSchema = academicYearBase.partial().superRefine(refineDateRange);
export type CreateAcademicYearInput = z.infer<typeof createAcademicYearSchema>;
export type UpdateAcademicYearInput = z.infer<typeof updateAcademicYearSchema>;

// ── Term / semester ────────────────────────────────────────────────────────────
const termBase = z.object({
  academicYearId: uuidSchema,
  code: codeSchema(20),
  name: nonEmptyTrimmed(100, 'Name'),
  termType: z.enum(TERM_TYPE_VALUES),
  sequence: z.number().int().min(1).max(12),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
  active: z.boolean().optional(),
});
export const createTermSchema = termBase.superRefine(refineDateRange);
export const updateTermSchema = termBase.partial().superRefine(refineDateRange);
export type CreateTermInput = z.infer<typeof createTermSchema>;
export type UpdateTermInput = z.infer<typeof updateTermSchema>;

// ── Course ─────────────────────────────────────────────────────────────────────
const units = z.number().min(0).max(99);
const hours = z.number().min(0).max(168);
const courseBase = z.object({
  institutionId: uuidSchema.optional(),
  code: codeSchema(30),
  title: nonEmptyTrimmed(200, 'Title'),
  description: optionalText(2000),
  departmentId: uuidSchema.nullable().optional(),
  lectureUnits: units.nullable().optional(),
  labUnits: units.nullable().optional(),
  lectureHoursPerWeek: hours.nullable().optional(),
  labHoursPerWeek: hours.nullable().optional(),
  active: z.boolean().optional(),
  ...effectiveDatesShape,
});
export const createCourseSchema = courseBase.superRefine(refineEffectiveRange);
export const updateCourseSchema = courseBase.partial().superRefine(refineEffectiveRange);
export type CreateCourseInput = z.infer<typeof createCourseSchema>;
export type UpdateCourseInput = z.infer<typeof updateCourseSchema>;

// ── Course section ─────────────────────────────────────────────────────────────
const courseSectionBase = z.object({
  courseId: uuidSchema,
  termId: uuidSchema,
  sectionCode: codeSchema(30),
  programId: uuidSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  capacity: z.number().int().min(0).max(10000).nullable().optional(),
  active: z.boolean().optional(),
});
export const createCourseSectionSchema = courseSectionBase;
export const updateCourseSectionSchema = courseSectionBase.partial();
export type CreateCourseSectionInput = z.infer<typeof createCourseSectionSchema>;
export type UpdateCourseSectionInput = z.infer<typeof updateCourseSectionSchema>;

// ── List queries ───────────────────────────────────────────────────────────────
const listBase = paginationQuerySchema.extend({ active: booleanQuerySchema.optional() });

export const listAcademicYearsQuerySchema = listBase.extend({
  status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
});
export const listTermsQuerySchema = listBase.extend({
  academicYearId: uuidSchema.optional(),
  status: z.enum(ACADEMIC_PERIOD_STATUS_VALUES).optional(),
});
export const listCoursesQuerySchema = listBase.extend({ departmentId: uuidSchema.optional() });
export const listCourseSectionsQuerySchema = listBase.extend({
  courseId: uuidSchema.optional(),
  termId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
});
